import { useState, useEffect, useCallback, useMemo } from 'react';

import { CheckCircle, Loader2, Plus, Plug, AlertCircle, ArrowLeft, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { connectorsApi } from '@/lib/api/connectors';
import { connectionsApi } from '@/lib/api/connections';
import { openOAuthPopup } from '@/lib/connector-oauth';
import { DynamicConnectionForm } from '@/components/connectors/DynamicConnectionForm';
import { ConnectorIcon } from '@/components/connectors/ConnectorIcon';

import type { Connector, Connection, UIField, AuthMethod } from '@optidev/shared-types';
import { UI_FIELD_TYPES } from '@optidev/shared-types';

interface ConnectionSetupHITLProps {
  connectorType: string;
  isClicked: boolean;
  isCancelled?: boolean;
  isStale?: boolean;
  onConnectionCreated?: (connectionId: string, connectorType: string, connectionName: string) => void;
  onCancelled?: () => void;
}

type Phase = 'loading' | 'select' | 'create' | 'error';

/**
 * ConnectionSetupHITL - Inline connection setup card rendered in agent chat.
 *
 * Flow:
 * 1. Fetches connector details + existing connections for the type
 * 2. If existing connections: shows selectable list + "Create new" option
 * 3. If no connections: shows DynamicConnectionForm directly
 * 4. On select/create: calls onConnectionCreated with the connection ID
 */
export function ConnectionSetupHITL({
  connectorType,
  isClicked,
  isCancelled,
  isStale,
  onConnectionCreated,
  onCancelled,
}: ConnectionSetupHITLProps) {
  const { getIdToken } = useAuth();

  const [phase, setPhase] = useState<Phase>('loading');
  const [connector, setConnector] = useState<Connector | null>(null);
  const [existingConnections, setExistingConnections] = useState<Connection[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);

  // OAuth + Resource Picker state (two-phase flow for connectors like Google Sheets)
  const [oauthAccessToken, setOauthAccessToken] = useState<string | null>(null);
  const [oauthConnectionId, setOauthConnectionId] = useState<string | null>(null);

  // Check if connector's OAuth method has a resource picker (e.g., Google Sheets spreadsheet picker)
  const connectorHasResourcePicker = useMemo(() => {
    if (!connector) return false;
    const oauthMethod = connector.uiSchema?.authMethods?.find(
      (m: AuthMethod) => m.id === 'oauth' || m.id === 'oauth2'
    );
    if (!oauthMethod) return false;
    const hasPickerField = oauthMethod.fields?.some(
      (f: UIField) => f.type === UI_FIELD_TYPES.RESOURCE_PICKER
    );
    return hasPickerField || oauthMethod.resourceScope === 'selected';
  }, [connector]);

  // Fetch connector + existing connections on mount
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const token = await getIdToken();
        if (!token || cancelled) return;

        // Fetch connector details and existing connections in parallel
        const [connectorData, connections] = await Promise.all([
          connectorsApi.getOne(token, connectorType),
          connectionsApi.getAll(token, { type: connectorType, enabled: true }),
        ]);

        if (cancelled) return;

        setConnector(connectorData);
        setExistingConnections(connections.filter(c => c.status === 'active'));

        // If there are existing connections, show the selection view
        // Otherwise, show the create form directly
        if (connections.filter(c => c.status === 'active').length > 0) {
          setPhase('select');
        } else {
          setPhase('create');
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const axiosErr = err as { response?: { data?: { message?: string } } };
        const message = axiosErr.response?.data?.message
          || (err instanceof Error ? err.message : 'Failed to load connector');
        console.error('[ConnectionSetupHITL] Load failed:', message);
        setErrorMessage(message);
        setPhase('error');
      }
    };

    load();
    return () => { cancelled = true; };
  }, [connectorType, getIdToken]);

  // Handle selecting an existing connection
  const handleSelectConnection = useCallback((connection: Connection) => {
    setSelectedConnectionId(connection.id);
    onConnectionCreated?.(connection.id, connectorType, connection.name);
  }, [connectorType, onConnectionCreated]);

  // Handle form submission for creating a new connection (API key / database / etc.)
  const handleFormSubmit = useCallback(async (formData: {
    name: string;
    authMethod?: string;
    credentials: Record<string, unknown>;
  }) => {
    if (!connector) return;

    setIsSubmitting(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Authentication required');

      const newConnection = await connectionsApi.create(token, {
        type: connectorType,
        name: formData.name,
        authMethod: formData.authMethod,
        credentials: formData.credentials,
      });

      setSelectedConnectionId(newConnection.id);
      onConnectionCreated?.(newConnection.id, connectorType, newConnection.name);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      const message = axiosError.response?.data?.message
        || (err instanceof Error ? err.message : 'Failed to create connection');
      console.error('[ConnectionSetupHITL] Create failed:', message);
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [connector, connectorType, getIdToken, onConnectionCreated]);

  // Handle OAuth flow
  const handleOAuthStart = useCallback(async (authMethod: string, connectionName?: string, additionalParams?: Record<string, string>) => {
    if (!connector) return;

    setIsSubmitting(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Authentication required');

      // Initiate OAuth flow (pass authMethod for scope overrides, e.g., 'oauth_readonly')
      const { authUrl } = await connectionsApi.initiateOAuth(
        token,
        connectorType,
        connectionName || `My ${connector.name} Connection`,
        undefined, // scopes - let backend resolve from authMethod
        authMethod,
        additionalParams,
      );

      // Open popup
      const result = await openOAuthPopup(authUrl);

      if (result.success && result.connectionId) {
        // Check if connector needs resource picker (e.g., Google Sheets file selection)
        if (connectorHasResourcePicker) {
          try {
            // Phase 2: Fetch access token for the resource picker
            const accessData = await connectionsApi.getAccess(token, result.connectionId);
            if (accessData.data?.accessToken) {
              setOauthAccessToken(accessData.data.accessToken as string);
              setOauthConnectionId(result.connectionId);
              // Form will re-render showing the resource picker
            } else {
              // Fallback: no access token, complete without resource selection
              setSelectedConnectionId(result.connectionId);
              onConnectionCreated?.(result.connectionId, connectorType, result.connectorName || connector.name);
            }
          } catch {
            // If access token fetch fails, still complete the connection
            setSelectedConnectionId(result.connectionId);
            onConnectionCreated?.(result.connectionId, connectorType, result.connectorName || connector.name);
          }
        } else {
          // No resource picker needed, complete immediately
          setSelectedConnectionId(result.connectionId);
          onConnectionCreated?.(
            result.connectionId,
            connectorType,
            result.connectorName || connector.name,
          );
        }
      } else if (result.error) {
        setErrorMessage(result.error);
      }
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      const message = axiosError.response?.data?.message
        || (err instanceof Error ? err.message : 'OAuth flow failed');
      console.error('[ConnectionSetupHITL] OAuth failed:', message);
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [connector, connectorType, connectorHasResourcePicker, getIdToken, onConnectionCreated]);

  // Handle resource picker submission (OAuth phase 2: save selectedResources to metadata)
  const handleResourcePickerSubmit = useCallback(async (formData: {
    name: string;
    authMethod?: string;
    credentials: Record<string, unknown>;
  }) => {
    if (!oauthConnectionId) return;

    setIsSubmitting(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Authentication required');

      // Update connection with selectedResources in metadata (not credentials)
      await connectionsApi.update(token, oauthConnectionId, {
        metadata: {
          selectedResources: formData.credentials.selectedResources,
        },
      } as Parameters<typeof connectionsApi.update>[2]);

      setSelectedConnectionId(oauthConnectionId);
      onConnectionCreated?.(oauthConnectionId, connectorType, connector?.name || connectorType);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      const message = axiosError.response?.data?.message
        || (err instanceof Error ? err.message : 'Failed to save resource selection');
      console.error('[ConnectionSetupHITL] Resource picker save failed:', message);
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [oauthConnectionId, connector, connectorType, getIdToken, onConnectionCreated]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    onCancelled?.();
  }, [onCancelled]);

  // -- Completed state (persisted across reloads via clickedActions) --
  if (isClicked || selectedConnectionId) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 w-fit">
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <CheckCircle className="w-3.5 h-3.5 text-green-500" />
          <span>Connection set up</span>
        </div>
      </div>
    );
  }

  // -- Cancelled state (persisted across reloads via clickedActions) --
  if (isCancelled) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 w-fit">
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <XCircle className="w-3.5 h-3.5 text-muted-foreground" />
          <span>Setup cancelled</span>
        </div>
      </div>
    );
  }

  // -- Stale state (user sent new message after this HITL) --
  if (isStale) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 w-fit">
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <span>Setup skipped</span>
        </div>
      </div>
    );
  }

  // -- Loading state --
  if (phase === 'loading') {
    return (
      <div className="bg-card border border-border rounded-lg p-5 w-full max-w-md">
        <div className="flex items-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Loading connector...</span>
        </div>
      </div>
    );
  }

  // -- Error state --
  if (phase === 'error') {
    return (
      <div className="bg-card border border-destructive/30 rounded-lg p-4 w-full max-w-md space-y-2">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-destructive" />
          <span className="text-sm font-medium">Failed to load connector</span>
        </div>
        <p className="text-xs text-muted-foreground">{errorMessage || 'Unknown error'}</p>
      </div>
    );
  }

  // -- Select existing connection or create new --
  if (phase === 'select' && connector) {
    return (
      <div className="bg-card border border-border rounded-lg w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-4 pt-4 pb-3 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
              <ConnectorIcon connector={connector} className="h-4.5 w-4.5" />
            </div>
            <div>
              <h4 className="text-sm font-medium">{connector.name}</h4>
              <p className="text-xs text-muted-foreground">Select a connection or create a new one</p>
            </div>
          </div>
        </div>

        {/* Connection list */}
        <div className="divide-y divide-border">
          {existingConnections.map((conn) => (
            <button
              key={conn.id}
              type="button"
              onClick={() => handleSelectConnection(conn)}
              className={cn(
                'w-full flex items-center justify-between px-4 py-3 text-left transition-colors',
                'hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none',
              )}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-green-500/10 flex-shrink-0">
                  <Plug className="w-3.5 h-3.5 text-green-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{conn.name}</p>
                  <p className="text-xs text-muted-foreground">Active</p>
                </div>
              </div>
              <span className="text-xs text-primary font-medium flex-shrink-0 ml-3">Use</span>
            </button>
          ))}
        </div>

        {/* Create new button */}
        <div className="px-4 py-3 border-t border-border">
          <button
            type="button"
            onClick={() => setPhase('create')}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors',
              'hover:bg-muted/50 border border-dashed border-border',
            )}
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-muted flex-shrink-0">
              <Plus className="w-3.5 h-3.5 text-muted-foreground" />
            </div>
            <span className="text-sm text-muted-foreground">Create new connection</span>
          </button>
        </div>

        {/* Cancel button */}
        <div className="px-4 pb-3">
          <button
            type="button"
            onClick={handleCancel}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Skip setup
          </button>
        </div>

        {/* Error banner */}
        {errorMessage && (
          <div className="px-4 pb-3">
            <p className="text-xs text-destructive">{errorMessage}</p>
          </div>
        )}
      </div>
    );
  }

  // -- Create new connection form --
  if (phase === 'create' && connector) {
    return (
      <div className="bg-card border border-border rounded-lg w-full max-w-md overflow-hidden">
        <div className="p-4">
          {/* Back button if user came from select phase */}
          {existingConnections.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setPhase('select');
                setErrorMessage('');
              }}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to connections
            </button>
          )}

          <DynamicConnectionForm
            connector={connector}
            onSubmit={oauthConnectionId ? handleResourcePickerSubmit : handleFormSubmit}
            onCancel={() => {
              if (oauthConnectionId) {
                setOauthAccessToken(null);
                setOauthConnectionId(null);
              } else if (existingConnections.length > 0) {
                setPhase('select');
              }
            }}
            onOAuthStart={handleOAuthStart}
            isSubmitting={isSubmitting}
            getAuthToken={getIdToken}
            accessToken={oauthAccessToken ?? undefined}
            connectionId={oauthConnectionId ?? undefined}
          />
        </div>

        {/* Cancel / Skip button */}
        <div className="px-4 pb-3">
          <button
            type="button"
            onClick={handleCancel}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Skip setup
          </button>
        </div>

        {/* Error banner */}
        {errorMessage && (
          <div className="px-4 pb-3">
            <p className="text-xs text-destructive">{errorMessage}</p>
          </div>
        )}
      </div>
    );
  }

  return null;
}
